<template>
    <div class="mt-3">
        <AllowPermission :permission="PERMISSION_CODES.CREATE_PROJECT_TASKS">
            <h2 class="mb-3" style="font-size: 18px">Create New Task</h2>
        </AllowPermission>
        <Button @click="$router.push('/project-tasks')"><i class="fas fa-chevron-left"></i> Back to List</Button>

        <!-- Client info bar (shown when a project with a client is selected) -->
        <div v-if="selectedClientInfo" class="tc-client-bar">
            <div class="tc-client-left">
                <div class="tc-client-icon"><i class="fas fa-id-card"></i></div>
                <div>
                    <div class="tc-client-label">Client</div>
                    <div class="tc-client-name">
                        {{ selectedClientInfo.name }}
                        <span v-if="selectedClientInfo.companyName" class="tc-client-company">{{ selectedClientInfo.companyName }}</span>
                    </div>
                </div>
            </div>
            <div class="tc-client-meta">
                <span v-if="selectedClientInfo.email">
                    <i class="fas fa-envelope"></i> {{ selectedClientInfo.email }}
                </span>
                <span v-if="selectedClientInfo.mobile">
                    <i class="fas fa-phone"></i> {{ selectedClientInfo.mobile }}
                </span>
                <span v-if="selectedClientInfo.city || selectedClientInfo.country">
                    <i class="fas fa-map-marker-alt"></i>
                    {{ [selectedClientInfo.city, selectedClientInfo.state, selectedClientInfo.country].filter(Boolean).join(', ') }}
                </span>
            </div>
        </div>

        <!-- Contact person selector (shown when selected client has a contact person) -->
        <div v-if="selectedClientInfo && selectedClientInfo.contactPersonName" class="tc-contact-bar">
            <div class="tc-contact-label"><i class="fas fa-user-tie"></i> Contact Person</div>
            <div class="tc-contact-select-wrap">
                <select v-model="selectedContactPerson" class="tc-contact-select">
                    <option value="">— None —</option>
                    <option :value="selectedClientInfo.id">
                        {{ selectedClientInfo.contactPersonName }}
                        <template v-if="selectedClientInfo.contactPersonPhone"> · {{ selectedClientInfo.contactPersonPhone }}</template>
                        <template v-if="selectedClientInfo.contactPersonEmail"> · {{ selectedClientInfo.contactPersonEmail }}</template>
                    </option>
                </select>
                <div v-if="selectedContactPerson && selectedClientInfo.contactPersonName" class="tc-contact-detail">
                    <span v-if="selectedClientInfo.contactPersonEmail">
                        <i class="fas fa-envelope"></i> {{ selectedClientInfo.contactPersonEmail }}
                    </span>
                    <span v-if="selectedClientInfo.contactPersonPhone">
                        <i class="fas fa-phone"></i> {{ selectedClientInfo.contactPersonPhone }}
                    </span>
                </div>
            </div>
        </div>

        <CrudIndex
            v-if="!loading"
            @created="$router.push('/project-tasks')"
            title="Task"
            :columnCount="1"
            :onlyCreate="true"
            :permission="PERMISSION_CODES.VIEW_PROJECT_TASKS"
            :viewColumns="columns"
            fetchUrl="/project-tasks"
            createUrl="/project-tasks"
            updateRootUrl="/project-tasks"
            deleteRootUrl="/project-tasks"
            :createFields="createFields"
            :editingFormatter="editingFormatter"
            @formDataUpdated="handleFormDataUpdate"
        />
        <div v-else>Please wait...</div>
    </div>
</template>

<script>
import CrudIndex from "@/components/crud/index"
import { createField, createColumn } from "@/components/crud/utils"
import { mapState, mapActions } from "vuex"
import { http } from "@/utils/http"
import Button from "@/components/UI/Button.vue"
import { PERMISSION_CODES } from "../../../store/permission"
import AllowPermission from "../../../components/layout/AllowPermission.vue"

export default {
    components: { AllowPermission, CrudIndex, Button },
    computed: {
        ...mapState("auth", ["user", "isAdmin"]),
        selectedClientInfo() {
            if (!this.currentClientId) return null
            return this.clients.find((c) => String(c.id) === String(this.currentClientId)) || null
        },
    },
    data() {
        return {
            PERMISSION_CODES,
            customers: {},
            products: {},
            currentCompanyId: "",
            currentProjectId: "",
            currentClientId: "",
            selectedContactPerson: "",
            orders: {},
            loading: false,
            columns: [createColumn("Id", "id", "string"), createColumn("Name", "name", "string")],
            createFields: [],
            taskCreateOptions: [],
            showPendingUsersSelection: false,
            minEndDate: "",
            maxEndDate: "",
            minInterimDate: "",
            maxInterimDate: "",
            endDateFilled: false,
            projectsToSelect: [],
            tasksToSelect: [],
            clients: [],
            defaultSelectedCompany: "",
            defaultSelectedProject: "",
            defaultSelectedTask: "",
        }
    },
    async mounted() {
        this.loading = true
        const [taskCreateOptions] = await Promise.all([
            this.fetchTaskCreateOptions(),
            this.fetchClients(),
        ])
        this.taskCreateOptions = taskCreateOptions

        const defaultCompanyId = this.$route.query.company_id
        const defaultProjectId = this.$route.query.project_id
        const defaultTaskId = this.$route.query.task_id

        if (defaultCompanyId) {
            this.defaultSelectedCompany = defaultCompanyId
            this.updateProjectsToSelect(defaultCompanyId)
        }
        if (defaultProjectId) {
            this.defaultSelectedProject = defaultProjectId
            await this.fetchTasks(defaultProjectId)
            this.updateClientFromProject(defaultProjectId)
        }
        if (defaultTaskId) {
            this.defaultSelectedTask = defaultTaskId
        }

        this.recreateFields()
        setTimeout(() => {
            this.loading = false
        }, 100)
    },
    methods: {
        ...mapActions("auth", ["checkRole"]),
        async fetchClients() {
            try {
                const { data } = await http.get("/project-clients")
                this.clients = data.result || []
            } catch (err) {
                console.error("Error fetching clients:", err)
            }
        },
        updateClientFromProject(projectId) {
            if (!projectId || !this.taskCreateOptions.projects) return
            const project = this.taskCreateOptions.projects.find((p) => String(p.id) === String(projectId))
            this.currentClientId = project?.clientId || ""
            this.selectedContactPerson = ""
        },
        delay(seconds = 1) {
            return new Promise((resolve) => setTimeout(() => resolve(true), seconds * 1000))
        },
        recreateFields(showMeasurement = true) {
            let creatorField
            if (this.isAdmin) {
                creatorField = createField(
                    "Creator*",
                    "reporterId",
                    "select",
                    true,
                    "",
                    this.taskCreateOptions.allUsers.map((item) => ({
                        id: item.id,
                        title: item.firstName,
                    }))
                )
            } else {
                creatorField = createField("Creator*", "reporterId", "hidden", true, this.user.id)
            }

            let createFields = [
                createField(
                    "Company*",
                    "companyId",
                    "select",
                    true,
                    this.defaultSelectedCompany,
                    this.taskCreateOptions.companies.map((item) => ({
                        id: item.id,
                        title: item.name,
                    }))
                ),
                createField(
                    "Project*",
                    "projectId",
                    "select",
                    true,
                    this.defaultSelectedProject,
                    this.projectsToSelect.map((item) => ({
                        id: item.id,
                        title: item.name,
                    }))
                ),
            ]

            if (showMeasurement) {
                createFields.push(createField("Measurement Value", "measurementValue", "number", false))
            }

            createFields.push(
                createField(
                    "Parent Task (Select if creating subtask)",
                    "taskId",
                    "select",
                    false,
                    this.defaultSelectedTask,
                    this.tasksToSelect.map((item) => ({
                        id: item.id,
                        title: item.name,
                    }))
                )
            )

            createFields = [
                ...createFields,
                createField(
                    "Status*",
                    "statusId",
                    "select",
                    true,
                    "",
                    this.taskCreateOptions.statuses.map((item) => ({
                        id: item.id,
                        title: item.name,
                    }))
                ),
                createField(
                    "Task Pending Due to Users*",
                    "pendingDueToUserIds",
                    this.showPendingUsersSelection ? "multiselect" : "hidden",
                    false,
                    "",
                    this.taskCreateOptions.allUsers.map((item) => ({
                        id: item.id,
                        title: item.firstName,
                    }))
                ),
                createField("Task Title*", "name", "text", true),
                creatorField,
                createField(
                    "Assignees*",
                    "assigneeUserIds",
                    "multiselect",
                    true,
                    "",
                    this.taskCreateOptions.allUsers
                        .filter((item) => this.currentCompanyId && item.companyId == this.currentCompanyId)
                        .map((item) => ({
                            id: item.id,
                            title: item.firstName,
                        }))
                ),
                createField(
                    "Report To*",
                    "reportToIds",
                    "multiselect",
                    true,
                    "",
                    this.taskCreateOptions.allUsers.map((item) => ({
                        id: item.id,
                        title: item.firstName,
                    }))
                ),
                createField("Start Date*", "startDate", "date", true, new Date().toISOString().slice(0, 10)),
                createField(
                    "Target Date (must be after start date)*",
                    "endDate",
                    `date|${this.minEndDate}|${this.maxEndDate}|disabled:false`,
                    true,
                    ""
                ),
                createField(
                    "Departments*",
                    "departmentIds",
                    "multiselect",
                    true,
                    "",
                    this.taskCreateOptions.departments.map((item) => ({
                        id: item.id,
                        title: item.name,
                    }))
                ),
                createField(
                    "Priority*",
                    "priorityId",
                    "select",
                    true,
                    "",
                    this.taskCreateOptions.priorities.map((item) => ({
                        id: item.id,
                        title: item.name,
                    }))
                ),
                createField("Attachment 1", "attachment1", "file", false),
                createField("Attachment 2", "attachment2", "file", false),
                createField("Attachment 3", "attachment3", "file", false),
                createField("Description", "description", "textarea", false),
            ]

            this.createFields = createFields
        },
        async fetchTasks(selectedId = "", val = null) {
            try {
                const res = await http.get(`/project-tasks?projectId=${selectedId || (val && val.projectId)}`)
                this.tasksToSelect = res.data.result || []
            } catch (err) {
                console.error("Error in loading tasks", err)
                this.$toast && this.$toast.error("Error in loading tasks")
            }
        },
        updateProjectsToSelect(companyId) {
            this.projectsToSelect = this.taskCreateOptions.projects.filter(
                (item) => item.companyIds && item.companyIds.split(",").includes(companyId)
            )
        },
        async handleFormDataUpdate(val) {
            if (!val) return

            if (val.projectId) {
                await this.fetchTasks("", val)
                this.currentProjectId = val.projectId
                this.updateClientFromProject(val.projectId)
                this.recreateFields()
            }

            if (val.statusId) {
                const pendingStatus = this.taskCreateOptions.statuses.find((item) => item.slug === "pending")
                if (pendingStatus && pendingStatus.id === val.statusId) {
                    this.showPendingUsersSelection = true
                } else {
                    this.showPendingUsersSelection = false
                }
                this.recreateFields()
            }

            if (val.startDate || val.endDate) {
                if (val.startDate) {
                    this.minEndDate = val.startDate
                    this.minInterimDate = val.startDate
                }
                if (val.endDate) {
                    this.maxInterimDate = val.endDate
                    this.endDateFilled = true
                }
                this.recreateFields()
            }

            if (!this.currentCompanyId || val.companyId != this.currentCompanyId) {
                this.currentCompanyId = val.companyId
                this.updateProjectsToSelect(val.companyId)
                this.recreateFields()
            }

            if (val.taskId) {
                this.recreateFields(false)
            }
        },
        editingFormatter(item) {
            return { ...item }
        },
        async fetchTaskCreateOptions() {
            try {
                const res = await http.get("/projects/masters?all_projects=true")
                return res.data
            } catch (err) {
                console.error("Error in loading task create options", err)
                return {}
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

/* Client info bar */
.tc-client-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: $radiusMd;
    padding: 12px 16px;
    margin: 12px 0 8px;
}
.tc-client-left {
    display: flex;
    align-items: center;
    gap: 10px;
}
.tc-client-icon {
    width: 34px;
    height: 34px;
    border-radius: $radiusSm;
    background: #dbeafe;
    color: #1d4ed8;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;
}
.tc-client-label {
    font-size: 11px;
    font-weight: 600;
    color: #3b82f6;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}
.tc-client-name {
    font-size: 14px;
    font-weight: 700;
    color: #1e3a8a;
}
.tc-client-company {
    font-size: 12px;
    font-weight: 400;
    color: #3b82f6;
    margin-left: 6px;
}
.tc-client-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    font-size: 12px;
    color: #1d4ed8;
    span {
        display: flex;
        align-items: center;
        gap: 5px;
    }
    i { font-size: 11px; }
}

/* Contact person bar */
.tc-contact-bar {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: $radiusMd;
    padding: 12px 16px;
    margin-bottom: 12px;
    flex-wrap: wrap;
}
.tc-contact-label {
    font-size: 12px;
    font-weight: 700;
    color: #15803d;
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    padding-top: 6px;
    i { font-size: 13px; }
}
.tc-contact-select-wrap {
    flex: 1;
    min-width: 220px;
}
.tc-contact-select {
    width: 100%;
    padding: 7px 10px;
    border: 1px solid #86efac;
    border-radius: $radiusSm;
    font-size: 13px;
    background: white;
    color: $gray800;
    outline: none;
    cursor: pointer;
    &:focus { border-color: #16a34a; }
}
.tc-contact-detail {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 6px;
    font-size: 12px;
    color: #15803d;
    span { display: flex; align-items: center; gap: 5px; }
    i { font-size: 11px; }
}

.card-text {
    font-size: 20px;
    font-weight: 600;
}
</style>
